/**
 * PR Status Dashboard Component for SWRM-30
 *
 * Provides comprehensive overview of PR monitoring activities including:
 * - Summary statistics and key metrics
 * - Active monitoring sessions
 * - Recent escalations and failures
 * - Real-time updates via WebSocket
 */

import React, { useState } from "react";
import {
  RefreshCw,
  AlertTriangle,
  Clock,
  GitPullRequest,
} from "lucide-react";
import {
  usePRMonitoringDashboard,
  usePRMonitoringSessions,
} from "../../hooks/usePRMonitoring";
import { MetricCard } from "../Common/MetricCard";
import { StatusBadge } from "../Common/StatusBadge";

interface PRStatusDashboardProps {
  className?: string;
}

export const PRStatusDashboard: React.FC<PRStatusDashboardProps> = ({
  className = "",
}) => {
  const {
    dashboard,
    loading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = usePRMonitoringDashboard();
  const {
    sessions,
    loading: sessionsLoading,
    error: sessionsError,
    refetch: refetchSessions,
  } = usePRMonitoringSessions();

  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([refetchDashboard(), refetchSessions()]);
    setRefreshing(false);
  };

  // Filter sessions for display
  const activeSessions = sessions.filter((s) => s.status === "active");
  const failedSessions = sessions.filter((s) => s.status === "failed");
  const escalatedSessions = sessions.filter((s) => s.escalation_count > 0);


  const formatDuration = (startedAt: string): string => {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    }
    return `${diffMinutes}m`;
  };

  if (dashboardError || sessionsError) {
    return (
      <div
        className={`bg-white rounded-lg shadow-sm border border-red-200 p-6 ${className}`}
      >
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Failed to Load Dashboard
          </h3>
          <p className="text-gray-600 mb-4">
            {dashboardError || sessionsError}
          </p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pr-status-dashboard" className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            PR Monitoring Dashboard
          </h1>
          <p className="text-gray-600">
            Real-time pull request status monitoring and management
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || dashboardLoading || sessionsLoading}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Active Sessions"
          value={dashboard?.active_monitoring_sessions || 0}
          icon={GitPullRequest}
          loading={dashboardLoading}
          trend={
            dashboard?.active_monitoring_sessions
              ? { value: 5, isPositive: true }
              : undefined
          }
        />

        <MetricCard
          title="Escalated"
          value={dashboard?.escalated_sessions || 0}
          icon={AlertTriangle}
          loading={dashboardLoading}
          trend={
            dashboard?.escalated_sessions
              ? { value: 2, isPositive: false }
              : undefined
          }
        />

        <MetricCard
          title="Recent Failures"
          value={dashboard?.recent_failures || 0}
          icon={AlertTriangle}
          loading={dashboardLoading}
          trend={
            dashboard?.recent_failures
              ? { value: 10, isPositive: false }
              : undefined
          }
        />

        <MetricCard
          title="Blocked Workflows"
          value={dashboard?.blocked_workflows || 0}
          icon={Clock}
          loading={dashboardLoading}
          trend={
            dashboard?.blocked_workflows
              ? { value: 3, isPositive: false }
              : undefined
          }
        />
      </div>

      {/* Active Sessions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Active Monitoring Sessions
          </h2>
          <p className="text-sm text-gray-600">
            Currently active PR monitoring sessions
          </p>
        </div>

        <div className="overflow-x-auto">
          {sessionsLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading sessions...</p>
            </div>
          ) : activeSessions.length === 0 ? (
            <div className="p-6 text-center">
              <GitPullRequest className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No active monitoring sessions</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PR
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workflow
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Escalations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PR Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activeSessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <GitPullRequest className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            #{session.pr_number}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-32">
                            {session.repository}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {session.workflow_name ||
                          `Workflow ${session.workflow_id}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge
                        status={
                          session.status === "active"
                            ? "running"
                            : session.status === "completed"
                            ? "completed"
                            : session.status === "failed"
                            ? "failed"
                            : "error"
                        }
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDuration(session.started_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {session.escalation_count > 0 ? (
                        <div className="flex items-center">
                          <AlertTriangle className="h-4 w-4 text-yellow-500 mr-1" />
                          <span className="text-sm text-yellow-700">
                            {session.escalation_count}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {session.pr_status ? (
                        <div className="flex items-center space-x-2">
                          <StatusBadge
                            status={
                              session.pr_status.ci_status === "pending"
                                ? "waiting"
                                : session.pr_status.ci_status === "success"
                                ? "healthy"
                                : session.pr_status.ci_status === "failure"
                                ? "failed"
                                : "error"
                            }
                          />
                          <StatusBadge
                            status={
                              session.pr_status.review_status === "pending"
                                ? "waiting"
                                : session.pr_status.review_status === "approved"
                                ? "healthy"
                                : "unhealthy"
                            }
                          />
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No data</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Escalations and Failures */}
      {(escalatedSessions.length > 0 || failedSessions.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Escalated Sessions */}
          {escalatedSessions.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-yellow-200">
              <div className="px-6 py-4 border-b border-yellow-200 bg-yellow-50">
                <h3 className="text-lg font-medium text-yellow-800 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Escalated Sessions ({escalatedSessions.length})
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {escalatedSessions.slice(0, 5).map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-3 bg-yellow-50 rounded-md"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          PR #{session.pr_number} -{" "}
                          {session.workflow_name ||
                            `Workflow ${session.workflow_id}`}
                        </div>
                        <div className="text-sm text-gray-600">
                          {session.escalation_count} escalation
                          {session.escalation_count > 1 ? "s" : ""} • Running
                          for {formatDuration(session.started_at)}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Level {session.escalation_count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Failed Sessions */}
          {failedSessions.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-red-200">
              <div className="px-6 py-4 border-b border-red-200 bg-red-50">
                <h3 className="text-lg font-medium text-red-800 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Failed Sessions ({failedSessions.length})
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {failedSessions.slice(0, 5).map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-3 bg-red-50 rounded-md"
                    >
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          PR #{session.pr_number} -{" "}
                          {session.workflow_name ||
                            `Workflow ${session.workflow_id}`}
                        </div>
                        <div className="text-sm text-gray-600">
                          {session.last_error || "Unknown error"} • Failed after{" "}
                          {formatDuration(session.started_at)}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <StatusBadge status="failed" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
